/**
 * Canonical relationship taxonomy guide — shared between:
 *  - ItemRelationshipActions (item detail tooltip modal)
 *  - RelationshipsHomePage (the /relationships landing page)
 *  - RelationshipDiscoverySheet
 *
 * Per canon 07-item-relationship-taxonomy-and-mutation-rules.
 */

import {
  Tag,
  Layers,
  Swords,
  ArrowDown,
  GitBranch,
  GitFork,
  Link2,
  Clock,
  RefreshCw,
  CornerDownRight,
  Weight,
} from 'lucide-react';
import type { FC } from 'react';

export interface RelationshipGuideEntry {
  id: string;
  label: string;
  axis: string;
  bond: string;
  /** Left accent stripe — shared in light and dark mode */
  accentBorder: string;
  /** @deprecated Use relationshipGuideCardClass — kept for light-mode fallbacks */
  color: string;
  iconColor: string;
  /** @deprecated Use relationshipGuideCardClass */
  colorDark: string;
  iconColorDark: string;
  Icon: FC<{ className?: string }>;
  tagline: string;
  description: string;
  examples: string[];
  whenNotTo: string;
  /** Compact list-chip abbreviation (item list Relationships column). */
  abbr?: string;
  /** Light-mode pill / chip surface classes (border + bg + text). */
  pillLight?: string;
  /** Dark-mode pill / chip surface classes. */
  pillDark?: string;
}

/** Neutral card surface + colored left accent (not full-bleed tint). */
export function relationshipGuideCardClass(
  isDark: boolean,
  entry: Pick<RelationshipGuideEntry, 'accentBorder'>,
): string {
  return isDark
    ? `rounded-lg border border-neutral-700 bg-[var(--surface)] border-l-4 ${entry.accentBorder} p-4 sm:p-5`
    : `rounded-lg border border-gray-200 bg-white border-l-4 ${entry.accentBorder} p-4 sm:p-5`;
}

export function discoverySectionCardClass(isDark: boolean, accentBorder: string): string {
  return isDark
    ? `rounded-xl border border-neutral-700 bg-[var(--surface)] border-l-4 ${accentBorder} p-4 sm:p-5`
    : `rounded-xl border border-gray-200 bg-white border-l-4 ${accentBorder} p-4 sm:p-5`;
}

export const RELATIONSHIP_GUIDE: RelationshipGuideEntry[] = [
  {
    id: 'cluster',
    label: 'Cluster',
    axis: 'Horizontal',
    bond: 'Identity',
    accentBorder: 'border-l-cyan-500',
    color: 'bg-cyan-50 border-cyan-200',
    iconColor: 'text-cyan-600',
    colorDark: 'border-cyan-500/40 bg-cyan-500/15',
    iconColorDark: 'text-cyan-300',
    Icon: Tag,
    abbr: 'Cl',
    pillLight: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    pillDark: 'border-cyan-500/40 bg-cyan-500/15 text-cyan-300',
    tagline: 'These items are really the same thing.',
    description:
      'Use a Cluster when two or more items describe the same underlying need or intent — just worded differently or submitted independently. A Cluster signals a deduplication decision: one item will become canonical, and the others are merged or suppressed. Clusters must be resolved before a Package can be created across the same items.',
    examples: ['Two tickets both requesting a dark mode toggle', '"Improve login speed" and "Reduce authentication latency"'],
    whenNotTo: "Don't cluster items that are merely related or complementary — use Package or Aggregation instead.",
  },
  {
    id: 'package',
    label: 'Package',
    axis: 'Horizontal',
    bond: 'Atomicity',
    accentBorder: 'border-l-orange-500',
    color: 'bg-orange-50 border-orange-200',
    iconColor: 'text-orange-600',
    colorDark: 'border-orange-500/40 bg-orange-500/15',
    iconColorDark: 'text-orange-300',
    Icon: Layers,
    abbr: 'Pk',
    pillLight: 'border-orange-200 bg-orange-50 text-orange-700',
    pillDark: 'border-orange-500/40 bg-orange-500/15 text-orange-300',
    tagline: 'These items must move together.',
    description:
      "A Package is an atomic group: the items inside it are prioritized as a unit and/or must be selected together. Use it when doing one item without the others would be incomplete or meaningless. Packages can be typed as \"Prioritize together\", \"Select together\", or both.",
    examples: ['Frontend, backend, and database work for a single feature', 'A research spike and the implementation it unblocks'],
    whenNotTo: "Don't use a Package when items are just thematically related. If you can ship them independently, they're not a Package.",
  },
  {
    id: 'exclusive',
    label: 'Competing',
    axis: 'Horizontal',
    bond: 'Mutual exclusivity',
    accentBorder: 'border-l-rose-500',
    color: 'bg-rose-50 border-rose-200',
    iconColor: 'text-rose-600',
    colorDark: 'border-rose-500/40 bg-rose-500/15',
    iconColorDark: 'text-rose-300',
    Icon: Swords,
    abbr: 'Cp',
    pillLight: 'border-rose-200 bg-rose-50 text-rose-700',
    pillDark: 'border-rose-500/40 bg-rose-500/15 text-rose-300',
    tagline: 'One or the other — not both.',
    description:
      'A Competing (Exclusive) group is a peer-level set where at most one member may receive a commitment-quality disposition. Use it when two or more distinct items draw on the same budget, slot, or outcome such that committing to both would be redundant or invalid. Unlike Package (all-or-nothing), Competing is at-most-one. Unlike Elaboration, there is no parent need or sufficiency continuum — the items sit at the same level.',
    examples: [
      '"Build in-house analytics" vs. "Buy a third-party analytics platform"',
      'Two architecture bets that cannot both be the chosen approach',
    ],
    whenNotTo: "Don't use Competing for items that are the same thing worded differently (Cluster) or possibilities along a sufficiency continuum for one parent need (Elaboration). Don't use it when all members must ship together (Package).",
  },
  {
    id: 'sequence',
    label: 'Sequence & conditions',
    axis: 'Horizontal',
    bond: 'Before this · Waiting on this',
    accentBorder: 'border-l-blue-500',
    color: 'bg-blue-50 border-blue-200',
    iconColor: 'text-blue-600',
    colorDark: 'border-blue-500/40 bg-blue-500/15',
    iconColorDark: 'text-blue-300',
    Icon: GitBranch,
    abbr: 'Sq',
    pillLight: 'border-blue-200 bg-blue-50 text-blue-700',
    pillDark: 'border-blue-500/40 bg-blue-500/15 text-blue-300',
    tagline: 'What must be addressed before this item, and what waits on it.',
    description:
      'Dependency and Prerequisite are true mirrors of the same predecessor→dependent structure. The UI folds them into one Sequence family. Stored edge types remain for audit and API compatibility; the add flow asks whether you are linking a predecessor or a dependent relative to the current item.',
    examples: ['Auth service must be addressed before SSO', 'Mobile client waits on finalized API design'],
    whenNotTo: "Don't confuse Sequence with Package (all-or-nothing) or Competing (at-most-one) — those are conceptual duals, not mirrors.",
  },
  {
    id: 'dependency',
    label: 'Dependency',
    axis: 'Horizontal',
    bond: 'Sequence / Condition',
    accentBorder: 'border-l-blue-500',
    color: 'bg-blue-50 border-blue-200',
    iconColor: 'text-blue-600',
    colorDark: 'border-blue-500/40 bg-blue-500/15',
    iconColorDark: 'text-blue-300',
    Icon: GitBranch,
    abbr: 'Sq',
    pillLight: 'border-blue-200 bg-blue-50 text-blue-700',
    pillDark: 'border-blue-500/40 bg-blue-500/15 text-blue-300',
    tagline: 'The succeeding item waits on a prerequisite.',
    description:
      'A Dependent is asserted from the downstream item: it cannot proceed until a preceding prerequisite is resolved. In the UI this is part of the Sequence family (see Sequence & conditions).',
    examples: ['Mobile client depends on finalized API design', 'SSO feature depends on deployed auth service'],
    whenNotTo: "Don't model every sequencing preference as a hard dependency. Reserve hard dependencies for genuine blockers.",
  },
  {
    id: 'prerequisite',
    label: 'Prerequisite',
    axis: 'Horizontal',
    bond: 'Preceding — must come first',
    accentBorder: 'border-l-blue-500',
    color: 'bg-blue-50 border-blue-200',
    iconColor: 'text-blue-600',
    colorDark: 'border-blue-500/40 bg-blue-500/15',
    iconColorDark: 'text-blue-300',
    Icon: GitBranch,
    abbr: 'Sq',
    pillLight: 'border-blue-200 bg-blue-50 text-blue-700',
    pillDark: 'border-blue-500/40 bg-blue-500/15 text-blue-300',
    tagline: 'The preceding item that must be satisfied first.',
    description:
      'A Prerequisite is asserted from the upstream item. In the UI this is part of the Sequence family with Dependency — same structure, different authorship viewpoint.',
    examples: ['Auth service declared as prerequisite for SSO and API gateway'],
    whenNotTo: "Don't create both a Dependency and a Prerequisite for the same pair — they describe the same constraint.",
  },
  {
    id: 'composition',
    label: 'Composition',
    axis: 'Vertical',
    bond: 'Whole and parts',
    accentBorder: 'border-l-emerald-500',
    color: 'bg-emerald-50 border-emerald-200',
    iconColor: 'text-emerald-600',
    colorDark: 'border-emerald-500/40 bg-emerald-500/15',
    iconColorDark: 'text-emerald-300',
    Icon: ArrowDown,
    abbr: 'Cm',
    pillLight: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    pillDark: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300',
    tagline: 'Whole and parts — one composition experience.',
    description:
      'Aggregation and Breakdown are true mirrors: both store the same parent→child whole-part structure. Direction alone does not encode Aggregation vs Breakdown — authorship viewpoint does. The UI folds them into Composition while preserving the authored edge type for audit.',
    examples: ['"Payments overhaul" composed of "Stripe integration", "invoice redesign", and "refund flow"'],
    whenNotTo: "Don't use Composition for items that must move together (Package) or compete (Competing) — those are conceptual duals, not mirrors.",
  },
  {
    id: 'aggregation',
    label: 'Aggregation',
    axis: 'Vertical',
    bond: 'Composition',
    accentBorder: 'border-l-emerald-500',
    color: 'bg-emerald-50 border-emerald-200',
    iconColor: 'text-emerald-600',
    colorDark: 'border-emerald-500/40 bg-emerald-500/15',
    iconColorDark: 'text-emerald-300',
    Icon: ArrowDown,
    abbr: 'Cm',
    pillLight: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    pillDark: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300',
    tagline: 'This item is made up of those items.',
    description:
      'Aggregation is bottom-up authorship of a whole-part link. In the UI this is part of the Composition family with Breakdown.',
    examples: ['"Payments overhaul" aggregates "Stripe integration", "invoice redesign", and "refund flow"'],
    whenNotTo: "Don't use Aggregation just to group related items. If they don't compose a whole, use Package or Collection instead.",
  },
  {
    id: 'breakdown',
    label: 'Breakdown',
    axis: 'Vertical',
    bond: 'Decomposition',
    accentBorder: 'border-l-emerald-500',
    color: 'bg-emerald-50 border-emerald-200',
    iconColor: 'text-emerald-600',
    colorDark: 'border-emerald-500/40 bg-emerald-500/15',
    iconColorDark: 'text-emerald-300',
    Icon: ArrowDown,
    abbr: 'Cm',
    pillLight: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    pillDark: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300',
    tagline: 'This item consists of those items.',
    description:
      'Breakdown is top-down authorship of a whole-part link. Structurally equivalent to Aggregation; the UI presents both as Composition.',
    examples: ['"Payments overhaul" breaks down into "Stripe integration", "invoice redesign", and "refund flow"'],
    whenNotTo: "Don't use Breakdown to model a continuum of possibilities for the same outcome (use Elaboration) or items that must move together (use Package).",
  },
  {
    id: 'contains',
    label: 'Contains',
    axis: 'Vertical',
    bond: 'Reporting hierarchy',
    accentBorder: 'border-l-sky-500',
    color: 'bg-sky-50 border-sky-200',
    iconColor: 'text-sky-600',
    colorDark: 'border-sky-500/40 bg-sky-500/15',
    iconColorDark: 'text-sky-400',
    Icon: CornerDownRight,
    abbr: 'Ct',
    pillLight: 'border-sky-200 bg-sky-50 text-sky-700',
    pillDark: 'border-sky-500/40 bg-sky-500/15 text-sky-300',
    tagline: 'This parent owns those items in the hierarchy.',
    description:
      'Contains is a passive parent→child link for navigation and reporting — like epic → story → task in Jira. It does not assert prioritization composition: no gate enforcement, no dependency surfacing at parent commit, no scope-completeness warnings. Use when you need a named parent item above children without structural decomposition weight.',
    examples: ['"Q3 Growth Initiative" contains "Improve onboarding" and "Launch referral program"', '"Platform reliability epic" contains three story-level items'],
    whenNotTo: "Don't use Contains when the parent genuinely decomposes into parts for prioritization — use Composition. Don't use it for flat thematic grouping without a parent item — use Collection.",
  },
  {
    id: 'elaboration',
    label: 'Elaboration',
    axis: 'Vertical',
    bond: 'Possibility spectrum',
    accentBorder: 'border-l-violet-500',
    color: 'bg-violet-50 border-violet-200',
    iconColor: 'text-violet-600',
    colorDark: 'border-violet-500/40 bg-violet-500/15',
    iconColorDark: 'text-violet-300',
    Icon: GitFork,
    abbr: 'El',
    pillLight: 'border-violet-200 bg-violet-50 text-violet-700',
    pillDark: 'border-violet-500/40 bg-violet-500/15 text-violet-300',
    tagline: 'A continuum of possibilities, from minimally sufficient to ideal.',
    description:
      "Elaboration answers, “How will you know when you have what you want?” by mapping a parent need to a continuum of possibilities — from minimally sufficient through sufficient to ideal. Those possibilities may be expressed as criteria or as solutions, and you typically select one rather than many. Use Solution Spectrum for different sufficiency, ambition, altitude, or investment levels; use Solution Variant for different approaches at a similar level. Unlike Competing, Elaboration has a parent need and describes its possibility continuum rather than imposing a peer-level at-most-one policy.",
    examples: ['"Reduce page load time" elaborated from "meet the 2-second target" through "instant-feeling navigation"', '"Enable enterprise auth" elaborated from "basic SAML support" through "a complete enterprise identity platform"'],
    whenNotTo: "Don't use Elaboration for constituent parts of a whole (Composition), items that must move together (Package), or peer items governed by an at-most-one policy (Competing).",
  },
  {
    id: 'collection',
    label: 'Collection',
    axis: 'Curatorial',
    bond: 'Curation',
    accentBorder: 'border-l-indigo-500',
    color: 'bg-indigo-50 border-indigo-200',
    iconColor: 'text-indigo-600',
    colorDark: 'border-indigo-500/40 bg-indigo-500/15',
    iconColorDark: 'text-indigo-300',
    Icon: Link2,
    abbr: 'Co',
    pillLight: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    pillDark: 'border-indigo-500/40 bg-indigo-500/15 text-indigo-300',
    tagline: 'I want to keep track of these items together.',
    description:
      "Collections are lightweight, non-structural groupings for organizational or thematic purposes. They carry no governance or prioritization implications — they're a way to curate a named set of items for reference, reporting, or communication (e.g. \"Q3 themes\", \"Accessibility work\", \"CEO watchlist\"). An item can belong to many collections. Unlike Contains, a Collection has no parent item.",
    examples: ['"North Star" collection of aspirational items', '"Customer-reported" collection for items originating from support tickets'],
    whenNotTo: "Don't use a Collection to express structural or causal relationships. Use Composition, Elaboration, Package, Contains, or Sequence for those.",
  },
  {
    id: 'reframe',
    label: 'Additional Category',
    axis: 'Category',
    bond: 'Perspective',
    accentBorder: 'border-l-rose-500',
    color: 'bg-rose-50 border-rose-200',
    iconColor: 'text-rose-600',
    colorDark: 'border-rose-500/40 bg-rose-500/15',
    iconColorDark: 'text-rose-300',
    Icon: RefreshCw,
    abbr: 'Re',
    pillLight: 'border-rose-200 bg-rose-50 text-rose-700',
    pillDark: 'border-rose-500/40 bg-rose-500/15 text-rose-300',
    tagline: 'The same item under more than one category.',
    description:
      "An additional category lets a single item be described as more than one category at once — for example, as both a Problem and an Opportunity. Each category has its own title, description, and altitude answers, but they all refer to the same underlying item. One category is always the lead; the others are additional. Categories are managed in the Classification section above.",
    examples: ['"Customer churn is high" as a Problem (fix the cause) and an Opportunity (reduce churn to grow ARR)', '"High deployment frequency" as a Solution (automate CI/CD) and an Investment (platform capability bet)'],
    whenNotTo: "Don't add a category just to duplicate an item. Each one should say something genuinely different about the same underlying thing — a different category, with its own wording for the title and description.",
  },
  {
    // Canon 12 §5.11; src/lib/carriesRelationships.ts holds the share and the reading rule.
    id: 'carries',
    label: 'Carries',
    axis: 'Horizontal',
    bond: 'Worth carried',
    // Tokens, not Tailwind hues (legacy-colors gate); slate is the neutral accent.
    accentBorder: 'border-l-[var(--slate)]',
    color: 'bg-[var(--slate-dim)] border-[var(--slate)]',
    iconColor: 'text-[var(--text-sec)]',
    colorDark: 'border-[var(--slate)] bg-[var(--slate-dim)]',
    iconColorDark: 'text-[var(--text-sec)]',
    Icon: Weight,
    abbr: 'Ca',
    pillLight: 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]',
    pillDark: 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]',
    tagline: 'This item carries part of the worth of the items it enables, and says how much.',
    description:
      "Carries records why an enabling item ranks where it does. The item is worth little on its own and much through what it makes possible, so it carries a share of the worth of each item it enables — say 40% of one, 25% of another. Later the rank can be read with its reason: a platform rebuild ranked third because it carries five of the top ten. Carries explains a rank. It does not say that one item depends on the other, that either must be chosen, or in what order the work is done, and it never feeds the capacity engine.",
    examples: ['"Platform rebuild" carries 40% of "Self-serve onboarding"', '"Data warehouse migration" carries 25% of "Usage-based billing"'],
    whenNotTo: "Don't use Carries to say one item cannot proceed without another (use Sequence) or that items must ship together (use Package). Carries is the reason for a rank, not a condition on it.",
  },
];

/** Item identity over time — documented on the Lineage tab, not in the relationship-types popover. */
export const LINEAGE_GUIDE_ENTRY: RelationshipGuideEntry = {
  id: 'lineage',
  label: 'Lineage',
  axis: 'Temporal',
  bond: 'Provenance',
  accentBorder: 'border-l-teal-500',
  color: 'bg-teal-50 border-teal-200',
  iconColor: 'text-teal-600',
  colorDark: 'border-teal-500/40 bg-teal-500/15',
  iconColorDark: 'text-teal-300',
  Icon: Clock,
  abbr: 'Li',
  pillLight: 'border-teal-200 bg-teal-50 text-teal-700',
  pillDark: 'border-teal-500/40 bg-teal-500/15 text-teal-300',
  tagline: 'This item originated from or transformed into another.',
  description:
    'Lineage tracks provenance over time — split, merge, fork, supersede, retire. It answers identity questions: where did this item come from, and what happened to its predecessors? Lineage is largely system-managed and lives on the Lineage tab, separate from the structural relationship graph.',
  examples: ['An item split during triage into a problem statement and a solution', 'A session-level item promoted into the main backlog'],
  whenNotTo: "Don't use lineage events to express similarity or sequencing between distinct items — use Cluster, Sequence, or Competing for those.",
};

/** Stored edge types folded into a UI family (true mirrors). */
export const RELATIONSHIP_UI_FAMILY: Record<string, string> = {
  dependency: 'sequence',
  prerequisite: 'sequence',
  aggregation: 'composition',
  breakdown: 'composition',
};

/** Primary UI-facing guide entries (mirrors folded; duals kept separate). */
export const RELATIONSHIP_UI_IDS = [
  'cluster',
  'package',
  'exclusive',
  'sequence',
  'composition',
  'contains',
  'elaboration',
  'collection',
  'reframe',
] as const;

export const RELATIONSHIP_UI_GUIDE: RelationshipGuideEntry[] = RELATIONSHIP_UI_IDS
  .map((id) => RELATIONSHIP_GUIDE.find((e) => e.id === id))
  .filter((e): e is RelationshipGuideEntry => Boolean(e));

export const RELATIONSHIP_TYPE_COUNT = RELATIONSHIP_UI_GUIDE.length;

/** Resolve stored or UI id to the guide entry used for icons/colors. */
export function getRelationshipGuideEntry(id: string): RelationshipGuideEntry | undefined {
  if (id === 'lineage') return LINEAGE_GUIDE_ENTRY;
  const familyId = RELATIONSHIP_UI_FAMILY[id] ?? id;
  return RELATIONSHIP_GUIDE.find((e) => e.id === familyId) ?? RELATIONSHIP_GUIDE.find((e) => e.id === id);
}

/** Light/dark pill classes for a relationship type or UI family. */
export function relationshipPillClass(id: string, isDark = false): string {
  const entry = getRelationshipGuideEntry(id);
  if (!entry) {
    return isDark
      ? 'border-neutral-500/40 bg-neutral-500/15 text-neutral-300'
      : 'border-neutral-200 bg-white text-neutral-700';
  }
  return isDark
    ? (entry.pillDark ?? `${entry.colorDark} ${entry.iconColorDark}`)
    : (entry.pillLight ?? `${entry.color} ${entry.iconColor}`);
}

/** Compact chip classes for the item-list Relationships column. */
export function relationshipChipClass(id: string): string {
  const entry = getRelationshipGuideEntry(id);
  return entry?.pillLight ?? 'bg-gray-50 text-gray-600 border-gray-200';
}

/** Edge-based types with a dedicated list page under /relationships */
export const RELATIONSHIP_TYPE_ROUTES: Record<string, string> = {
  cluster: '/prioritization/relationships/clusters',
  package: '/prioritization/relationships/packages',
  exclusive: '/prioritization/relationships/exclusives',
  sequence: '/prioritization/relationships/sequence',
  dependency: '/prioritization/relationships/sequence',
  prerequisite: '/prioritization/relationships/sequence',
  composition: '/prioritization/relationships/composition',
  aggregation: '/prioritization/relationships/composition',
  breakdown: '/prioritization/relationships/composition',
  contains: '/prioritization/relationships/contains',
  elaboration: '/prioritization/relationships/elaborations',
  collection: '/prioritization/collections',
  reframe: '/prioritization/relationships/reframes',
  lineage: '/prioritization/relationships/lineage',
};
